import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Award, ArrowLeft, Download, Share2, GraduationCap, 
  CheckCircle, Calendar, Hash, Loader2
} from 'lucide-react';
import { format } from 'date-fns';

const TechLearningCertificate = () => {
  const { certificateId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const certificateRef = useRef(null);
  
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCertificate = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/tech-learning/certificates/${certificateId}`);
      setCertificate(response.data.certificate);
    } catch (error) {
      console.error('Failed to fetch certificate:', error);
    } finally {
      setLoading(false);
    }
  }, [api, certificateId]);

  useEffect(() => {
    fetchCertificate();
  }, [fetchCertificate]);

  const handleDownload = () => {
    // In a real implementation, this would generate a PDF
    window.print();
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Certificate - ${certificate.course_title}`,
          text: `I completed ${certificate.course_title} on Tech Learning!`,
          url: window.location.href
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback to copying link
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </MainLayout>
    );
  }

  if (!certificate) {
    return (
      <MainLayout>
        <Card className="p-12 text-center">
          <Award className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
          <h2 className="text-2xl font-bold mt-4">Certificate Not Found</h2>
          <Button className="mt-4" onClick={() => navigate('/tech-learning/my-courses')}>
            Back to My Courses
          </Button>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="certificate-view">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/tech-learning/my-courses')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to My Courses
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
            <Button onClick={handleDownload}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        </div>

        {/* Certificate */}
        <Card 
          ref={certificateRef}
          className="overflow-hidden print:shadow-none"
          data-testid="certificate-card"
        >
          <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white p-8 md:p-12">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
              <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl" />
            </div>
            
            {/* Border */}
            <div className="absolute inset-4 border-2 border-amber-500/30 rounded-lg pointer-events-none" />
            
            <div className="relative text-center space-y-8">
              {/* Header */}
              <div className="flex items-center justify-center gap-3">
                <GraduationCap className="h-10 w-10 text-amber-400" />
                <span className="text-xl font-semibold text-amber-400 tracking-wider uppercase">
                  Tech Learning
                </span>
              </div>

              {/* Title */}
              <div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                  Certificate of Completion
                </h1>
                <p className="text-slate-400 mt-2">This certifies that</p>
              </div>

              {/* Name */}
              <div className="py-4">
                <div className="text-3xl md:text-4xl font-serif font-bold text-amber-300">
                  {certificate.user_name}
                </div>
                <div className="w-48 h-0.5 bg-amber-500/50 mx-auto mt-4" />
              </div>

              {/* Course */}
              <div>
                <p className="text-slate-400">has successfully completed the course</p>
                <h2 className="text-2xl md:text-3xl font-bold mt-2">
                  {certificate.course_title}
                </h2>
              </div>

              {/* Details */}
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  <Calendar className="h-4 w-4 text-amber-400" />
                  <span>{format(new Date(certificate.issue_date), 'MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span>Score: {certificate.final_score}%</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <Hash className="h-4 w-4 text-amber-400" />
                  <span className="font-mono">{certificate.certificate_number}</span>
                </div>
              </div>

              {/* Award Icon */}
              <div className="pt-4">
                <div className="inline-flex items-center justify-center h-20 w-20 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg">
                  <Award className="h-10 w-10 text-white" />
                </div>
              </div>

              {/* Footer */}
              <div className="pt-4 border-t border-slate-700">
                <div className="flex items-center justify-center gap-2 text-sm text-slate-400">
                  <span>Verified Digital Certificate</span>
                  <span>•</span>
                  <span>Powered by Smart Learner Pro</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Certificate Details */}
        <Card>
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold mb-4">Certificate Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-sm text-muted-foreground">Certificate Number</div>
                <div className="font-mono">{certificate.certificate_number}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Issue Date</div>
                <div>{format(new Date(certificate.issue_date), 'MMMM d, yyyy')}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Course</div>
                <div>{certificate.course_title}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Final Score</div>
                <div>{certificate.final_score}%</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Lessons Completed</div>
                <div>{certificate.completed_lessons} of {certificate.total_lessons}</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Recipient</div>
                <div>{certificate.user_name}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [data-testid="certificate-card"],
          [data-testid="certificate-card"] * {
            visibility: visible;
          }
          [data-testid="certificate-card"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
      `}</style>
    </MainLayout>
  );
};

export default TechLearningCertificate;
